import React, { ReactNode } from "react"
import classNames from "classnames"

import RerollIcon from "./reroll_icon"
import { MOVE_LABELS, MoveKey } from "./types"

const MOVE_ACCENT: Record<MoveKey, string> = {
  read: "bg-sky-500",
  extract: "bg-amber-500",
  place: "bg-violet-500",
  polish: "bg-teal-500",
  wander: "bg-rose-500",
  project: "bg-emerald-500",
}

const MOVE_LABEL: Record<MoveKey, string> = {
  read: "text-sky-700 dark:text-sky-300",
  extract: "text-amber-800 dark:text-amber-300",
  place: "text-violet-700 dark:text-violet-300",
  polish: "text-teal-800 dark:text-teal-300",
  wander: "text-rose-700 dark:text-rose-300",
  project: "text-emerald-700 dark:text-emerald-300",
}

export default function MoveCard({
  move,
  onReroll,
  onPrimary,
  children,
  actions,
  className,
  showReroll = true,
}: {
  move: MoveKey
  onReroll: () => void
  onPrimary: () => void
  children: ReactNode
  actions?: ReactNode
  className?: string
  showReroll?: boolean
}) {
  return (
    <article
      className={classNames(
        "group relative flex overflow-hidden rounded-lg",
        "bg-white dark:bg-gray-900",
        "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_12px_32px_-12px_rgba(15,23,42,0.18)]",
        "ring-1 ring-stone-900/[0.08] dark:ring-white/10",
        "transition-[transform,box-shadow] duration-200 ease-out",
        "hover:-translate-y-0.5 hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_20px_40px_-14px_rgba(15,23,42,0.22)]",
        className,
      )}
      data-testid={`now-card-${move}`}
    >
      <div className={classNames("w-1.5 shrink-0", MOVE_ACCENT[move])} aria-hidden />

      <div className="flex min-w-0 flex-1 flex-col bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-0.5">
          <span
            className={classNames(
              "text-[11px] font-semibold uppercase tracking-[0.14em]",
              MOVE_LABEL[move],
            )}
          >
            {MOVE_LABELS[move]}
          </span>
          {showReroll && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onReroll()
              }}
              aria-label="Another one"
              title="Another one"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded border border-gray-300 bg-gray-50 text-zinc-600 transition-colors hover:bg-sky-100 hover:text-zinc-700 dark:border-gray-500 dark:bg-gray-600 dark:text-zinc-300 dark:hover:bg-sky-800 dark:hover:text-zinc-100"
              data-testid={`now-reroll-${move}`}
            >
              <RerollIcon />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onPrimary}
          className="flex min-h-0 flex-1 flex-col items-stretch px-5 pb-4 pt-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400/60"
        >
          {children}
        </button>

        {actions && (
          <div
            className="flex flex-wrap items-center gap-2 border-t border-stone-100 bg-stone-50/80 px-5 py-3 dark:border-white/10 dark:bg-black/25"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
    </article>
  )
}

export function CardPrimaryButton({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "inline-flex h-9 items-center rounded-full px-4 text-[13px] font-medium",
        "bg-stone-200/90 text-stone-800",
        "transition-[background-color,transform] duration-150",
        "hover:bg-stone-300/90 hover:text-stone-950",
        "active:scale-[0.98]",
        "dark:bg-white/12 dark:text-stone-100",
        "dark:hover:bg-white/18 dark:hover:text-white",
      )}
    >
      {children}
    </button>
  )
}

export function CardSecondaryButton({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "inline-flex h-9 items-center rounded-full px-3.5 text-[13px] font-medium",
        "bg-white text-stone-700",
        "ring-1 ring-inset ring-stone-200",
        "transition-colors hover:bg-stone-50 hover:text-stone-950",
        "dark:bg-transparent dark:text-stone-300 dark:ring-white/15",
        "dark:hover:bg-white/[0.06] dark:hover:text-white",
      )}
    >
      {children}
    </button>
  )
}

export function CardPreviewText({
  children,
  lines = 6,
  className,
}: {
  children: ReactNode
  lines?: number
  className?: string
}) {
  return (
    <p
      className={classNames(
        "text-[15px] leading-[1.55] text-stone-700 dark:text-stone-200",
        lines === 3 && "line-clamp-3",
        lines === 4 && "line-clamp-4",
        lines === 5 && "line-clamp-5",
        lines === 6 && "line-clamp-6",
        className,
      )}
    >
      {children}
    </p>
  )
}

export function CardMeta({ children }: { children: ReactNode }) {
  return (
    <div className="mt-auto flex flex-wrap items-center gap-2 pt-4 text-[12px] text-stone-500 dark:text-stone-400">
      {children}
    </div>
  )
}

/**
 * Plain-text preview from HTML.
 * Block boundaries and breaks become spaces so words never run together.
 */
export function previewFromHtml(
  html: string | null | undefined,
  max = 520,
): string {
  if (!html) return ""
  const spaced = html
    .replace(/<\s*br\s*\/?>/gi, " ")
    .replace(
      /<\/(p|div|li|h[1-6]|tr|blockquote|pre|td|th|section|article)>/gi,
      " ",
    )
    .replace(
      /<(p|div|li|h[1-6]|tr|blockquote|pre|td|th|section|article)(\s[^>]*)?>/gi,
      " ",
    )
    .replace(/&nbsp;/gi, " ")
  const doc = new DOMParser().parseFromString(spaced.slice(0, 8000), "text/html")
  const text = (doc.body.textContent || "").replace(/\s+/g, " ").trim()
  if (text.length <= max) return text
  return `${text.slice(0, max).trim()}…`
}

/** Display title for a source: prefer human names over raw URLs. */
export function sourceDisplayTitle(source: {
  name?: string | null
  userProvidedName?: string | null
  scannedName?: string | null
  url?: string | null
}): string {
  const named =
    source.userProvidedName || source.name || source.scannedName || null
  if (named && named.trim()) return named.trim()
  if (source.url) {
    try {
      const u = new URL(source.url)
      const path = u.pathname === "/" ? "" : u.pathname
      const host = u.hostname.replace(/^www\./, "")
      // Prefer host + short path over full query-string mess when no title
      if (path && path.length < 40) return `${host}${path}`
      return host
    } catch {
      return source.url
    }
  }
  return "Untitled source"
}
